// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles, IRawStyle, ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import {
  concatStyleSets,
  DefaultFontStyles,
  getHighContrastNoAdjustStyle,
  HighContrastSelector,
  memoizeFunction,
} from "@fluentui/react";
import type {
  IContextualMenuStyles,
  IMenuItemStyles,
} from "@fluentui/react/lib/ContextualMenu";

import { isThemingEnabled } from "@1js/acui-theme";

import type { ActionControlSemanticColors } from "./actionControlSemanticColors";
import { getActionControlSemanticColorsWithTheme } from "./actionControlSemanticColors";
import type { StandardControlSemanticColors } from "./standardControlSemanticColors";
import { getStandardControlSemanticColorsWithTheme } from "./standardControlSemanticColors";
import type { AppColors } from "../utilities/appColors";
import { controlPalette } from "./controlPalette";
import { buttonBorderWidth } from "./commonStylesBase";

export const modernUxControlsBorderRadius: number = 4;

export const baseFabricFocusVisibleSelector: string =
  ".ms-Fabric--isFocusVisible";
export const baseFabricHoverFocusSelector: string = `${baseFabricFocusVisibleSelector} &:focus:hover`;
export const baseFabricFocusSelector: string = `${baseFabricFocusVisibleSelector} &:focus`;
export const baseFabricFocusAfterSelector: string = `${baseFabricFocusVisibleSelector} &:focus:after`;
export const baseFabricHoverFocusAfterSelector: string = `${baseFabricFocusVisibleSelector} &:focus:hover:after`;
export const baseFabricHoverSelector: string = `${baseFabricFocusVisibleSelector} &:hover`;

export const commonHighContrastOutlineHoverFocus: IRawStyle = {
  outline: "2px dashed transparent",
};
export const commonHighContrastOutlineToggledOn: IRawStyle = {
  outline: "2px solid transparent",
};
export const commonHighContrastOutlineFocusedDisabled: IRawStyle = {
  outline: "2px dotted transparent",
};
export const commonHighContrastBorder: IRawStyle = {
  border: "1px solid transparent",
};
export const commonCalloutBoxShadow: IRawStyle = {
  boxShadow: "0 4px 8px 0px rgba(0, 0, 0, 0.1)",
};

export const commonMenuItemHighContrastOutlineHoverFocus: IRawStyle = {
  outline: "4px dashed transparent",
  outlineOffset: "-4px",
};

export const buttonLabelHorizontalMargin: number = 4; // the margin on the left and right of the label within a base button

/**
 * We need to leave space for scroll bar in galleries even though the scroll is not shown
 * Related Bugs: 5641993, 5663721
 */
export const scrollBarWidth = 18;

// For reduced motion, we use 0.001 instead of 0 so that any components
// that require animations to show will still do so.
export const defaultAnimationDurationMs = 325;
export const prefersReducedMotionQuery: string =
  "@media (prefers-reduced-motion: reduce)";
export const prefersReducedMotionDurationMs: number = 0.001;
export const prefersReducedMotionDuration: string = `${prefersReducedMotionDurationMs}ms !important`;

export const getDefaultAnimationDuration = (
  reduceAnimation?: boolean
): number => {
  return reduceAnimation
    ? prefersReducedMotionDurationMs
    : defaultAnimationDurationMs;
};

export const commonReducedMotionStyles: IRawStyle = {
  selectors: {
    [prefersReducedMotionQuery]: {
      transitionDuration: prefersReducedMotionDuration,
    },
  },
};

export function getHighContrastAutoAdjustStyle(): IRawStyle {
  return {
    forcedColorAdjust: "auto",
    MsHighContrastAdjust: "auto",
  };
}

export const commonHighContrastOutlineHoverDisabled: IRawStyle = {
  outline: "none",
};

export const commonHighContrastHoverFocusStyles: IRawStyle = {
  selectors: {
    [HighContrastSelector]: {
      backgroundColor: "Highlight",
      color: "HighlightText",
      outlineColor: "ButtonText",
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

export const commonHighContrastButtonRestStyles: IRawStyle = {
  selectors: {
    [HighContrastSelector]: {
      backgroundColor: "ButtonFace",
      color: "ButtonText",
      outlineColor: "inherit",
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

export const commonHighContrastDisabledStyles: IRawStyle = {
  selectors: {
    [HighContrastSelector]: {
      backgroundColor: "ButtonFace",
      color: "GrayText",
      borderColor: "GrayText",
      opacity: 1,
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

export const commonHighContrastFocusedDisabledStyles: IRawStyle = {
  selectors: {
    [HighContrastSelector]: {
      backgroundColor: "Highlight",
      color: "GrayText",
      borderColor: "GrayText",
      opacity: 1,
      outlineColor: "GrayText",
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

export const commonHighContrastCanvasSubStyles: IRawStyle = {
  background: "Canvas",
  color: "CanvasText",
};

/** Task 4896327: Deprecate Window/WindowText Styling
 * We need this and the sub styling to support HC styling in
 * both IE and Chromium. Once we no longer support IE,
 * we can clean this up */

// Note: If you are using this styling on a text container with links,
// be sure that your links are still using correct high contrast color
export const commonHighContrastCanvasStyles: IRawStyle = {
  selectors: {
    [HighContrastSelector]: {
      ...commonHighContrastCanvasSubStyles,
      background: "Window",
      color: "WindowText",
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

export const commonHighContrastCanvasTextSubStyles: IRawStyle = {
  color: "CanvasText",
};

/** Task 4896327: Deprecate Window/WindowText Styling
 * We need this and the sub styling to support HC styling in
 * both IE and Chromium. Once we no longer support IE,
 * we can clean this up */
export const commonHighContrastCanvasTextStyles: IRawStyle = {
  selectors: {
    [HighContrastSelector]: {
      ...commonHighContrastCanvasTextSubStyles,
      color: "WindowText",
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

export const commonHighContrastNoAdjustStyles: IRawStyle = {
  selectors: {
    [HighContrastSelector]: {
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

export const commonHighContrastHoveredDisabledStyles: IRawStyle = {
  selectors: {
    [HighContrastSelector]: {
      backgroundColor: "transparent",
    },
  },
};

export const getFocusStyle = memoizeFunction(
  (
    inset: number = 0,
    position: "relative" | "absolute" = "relative"
  ): IRawStyle => {
    return {
      outline: "transparent",
      position,

      selectors: {
        "::-moz-focus-inner": {
          border: "0",
        },

        [baseFabricFocusAfterSelector]: {
          content: '""',
          position: "absolute",
          left: inset + 1,
          top: inset + 1,
          bottom: inset + 1,
          right: inset + 1,
          border: `${buttonBorderWidth}px solid #ffffff`,
          outline: "1px solid #666666",
          zIndex: 1,
        },
      },
    };
  }
);

export const hiddenContentClassName = "acui-hidden-content";

export const hiddenContentStyle: IRawStyle = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: "0px",
  border: "0px",
  overflow: "hidden",
};

const noOutline: IRawStyle = {
  outline: 0,
};

const iconStyle = {
  fontSize: "16px",
  margin: "0 4px",
  height: "16px",
  lineHeight: "16px",
  textAlign: "center",
  verticalAlign: "middle",
  flexShrink: 0,
};

export const getBaseButtonStyles = memoizeFunction(
  (theme: ITheme): IButtonStyles => {
    const semanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    return {
      root: [
        getFocusStyle(-1),
        DefaultFontStyles.medium,
        {
          boxSizing: "border-box",
          border: `${buttonBorderWidth}px solid transparent`,
          userSelect: "none",
          display: "inline-block",
          textDecoration: "none",
          textAlign: "center",
          cursor: "pointer",
          verticalAlign: "top",
          padding: "0 16px",
          borderRadius: 0,
        },
      ],

      rootDisabled: [
        getFocusStyle(-1),
        {
          backgroundColor: isThemingEnabled()
            ? semanticColors.defaultBackgroundColor
            : controlPalette.defaultBackgroundColor,
          color: isThemingEnabled()
            ? semanticColors.textDisabledColor
            : "#a6a6a6",
          cursor: "default",
          pointerEvents: "none",
          selectors: {
            ":hover": noOutline,
            ":focus": [
              commonHighContrastFocusedDisabledStyles,
              commonHighContrastOutlineFocusedDisabled,
            ],
          },
        },
        commonHighContrastDisabledStyles,
      ],

      iconDisabled: {
        color: isThemingEnabled()
          ? semanticColors.textDisabledColor
          : "#a6a6a6",
      },

      menuIconDisabled: {
        color: isThemingEnabled()
          ? semanticColors.textDisabledColor
          : "#a6a6a6",
      },

      flexContainer: {
        display: "flex",
        height: "100%",
        flexWrap: "nowrap",
        justifyContent: "center",
        alignItems: "center",
      },

      textContainer: {
        flexGrow: 1,
      },

      icon: iconStyle,

      menuIcon: [
        iconStyle,
        {
          fontSize: "12px",
        },
      ],

      label: {
        margin: `0 ${buttonLabelHorizontalMargin}px`,
        lineHeight: "100%",
      },
      labelDisabled: {
        selectors: {
          [HighContrastSelector]: {
            color: "grayText",
          },
        },
      },

      screenReaderText: hiddenContentStyle,
    };
  }
);

export const getContextualMenuStyles = memoizeFunction(
  (theme: ITheme, appColors?: AppColors): IContextualMenuStyles => {
    const semanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    const styles: IContextualMenuStyles = {
      root: {
        backgroundColor: semanticColors.defaultBackgroundColor,
        minWidth: "180px",
      },
      container: {},
      list: {
        listStyleType: "none",
        margin: "0",
        padding: "0",
        lineHeight: "0",
      },
      title: {
        fontSize: "16px",
        paddingRight: "14px",
        paddingLeft: "14px",
        paddingBottom: "5px",
        paddingTop: "5px",
        backgroundColor: "#eaeaea",
      },
      header: [
        DefaultFontStyles.small,
        {
          fontWeight: 600,
          color: appColors && appColors.primary,
          background: "none",
          border: "none",
          height: 32,
          lineHeight: 32,
          cursor: "default",
          padding: "0px 6px",
          userSelect: "none",
          textAlign: "left",
        },
      ],
      subComponentStyles: {
        menuItem: {},
        callout: {},
      },
    };
    // Fabric issue #6922: there's a bug with concatStyleSets and mergeStyles that
    // gives the wrong type for IContextualMenuClassName
    return {
      ...concatStyleSets(styles),
      subComponentStyles: {
        menuItem: {},
        callout: {},
      },
    };
  }
);

const getItemHighContrastStyles = memoizeFunction((disabled): IRawStyle => {
  return {
    selectors: {
      [HighContrastSelector]: {
        backgroundColor: "Highlight",
        borderColor: "Highlight",
        color: disabled ? "GrayText" : "HighlightText",
        ...getHighContrastNoAdjustStyle(),
      },
    },
  };
});

export const getMenuItemStyles = memoizeFunction(
  (theme: ITheme, appColors: AppColors, disabled: boolean): IMenuItemStyles => {
    const semanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    const ContextualMenuItemBackgroundHoverColor = isThemingEnabled()
      ? semanticColors.hoverBackgroundColor
      : controlPalette.defaultBackgroundColor;
    const ContextualMenuItemBackgroundSelectedColor = isThemingEnabled()
      ? semanticColors.toggledBackgroundColor
      : "#eaeaea";
    const ContextualMenuItemDividerColor = isThemingEnabled()
      ? semanticColors.itemDividerColor
      : "#c8c8c8";
    const ContextualMenuItemHeight = "32px";

    const menuItemStyles: IMenuItemStyles = {
      item: [
        DefaultFontStyles.medium,
        {
          color: "#333333",
          position: "relative",
          boxSizing: "border-box",
        },
      ],
      divider: {
        display: "block",
        height: "1px",
        backgroundColor: ContextualMenuItemDividerColor,
        position: "relative",
      },
      root: [
        getFocusStyle(),
        {
          font: "inherit",
          color: "inherit",
          backgroundColor: "transparent",
          border: "none",
          width: "100%",
          height: ContextualMenuItemHeight,
          lineHeight: ContextualMenuItemHeight,
          display: "block",
          cursor: "pointer",
          padding: "0px 6px",
          textAlign: "left",
        },
      ],
      rootDisabled: {
        color: ContextualMenuItemDividerColor,
        cursor: "default",
        pointerEvents: "none",
        selectors: {
          [HighContrastSelector]: {
            color: "grayText",
          },
          ":hover": commonHighContrastOutlineHoverDisabled,
        },
      },
      rootHovered: [
        {
          backgroundColor: ContextualMenuItemBackgroundHoverColor,
          ...getItemHighContrastStyles(disabled),
        },
        commonHighContrastOutlineHoverDisabled,
      ],
      rootFocused: {
        backgroundColor: ContextualMenuItemBackgroundHoverColor,
        ...getItemHighContrastStyles(disabled),
      },
      rootChecked: {
        ...getItemHighContrastStyles(disabled),
      },
      rootPressed: {
        backgroundColor: ContextualMenuItemBackgroundSelectedColor,
        ...getItemHighContrastStyles(disabled),
      },
      rootExpanded: {
        backgroundColor: ContextualMenuItemBackgroundSelectedColor,
        color: "#000000",
        ...getItemHighContrastStyles(disabled),
      },
      linkContent: {
        whiteSpace: "nowrap",
        height: "inherit",
        display: "flex",
        alignItems: "center",
        maxWidth: "100%",
      },
      anchorLink: {
        padding: "0px 6px",
        textRendering: "auto",
        color: "inherit",
        letterSpacing: "normal",
        wordSpacing: "normal",
        textTransform: "none",
        textIndent: "0px",
        textShadow: "none",
        textDecoration: "none",
        boxSizing: "border-box",
      },
      label: {
        margin: "0 4px",
        verticalAlign: "middle",
        display: "inline-block",
        flexGrow: "1",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        selectors: {
          [HighContrastSelector]: {
            color: disabled ? "grayText" : undefined,
          },
        },
      },
      labelDisabled: {
        selectors: {
          [HighContrastSelector]: {
            color: "grayText",
          },
        },
      },
      icon: {
        display: "inline-block",
        minHeight: "1px",
        maxHeight: ContextualMenuItemHeight,
        width: "14px",
        margin: "0 4px",
        verticalAlign: "middle",
        flexShrink: "0",
      },
      iconColor: {
        color: appColors.primary,
        selectors: {
          [HighContrastSelector]: {
            color: "HighlightText",
          },
        },
      },
      iconDisabled: {
        color: ContextualMenuItemDividerColor,
      },
      checkmarkIcon: {
        color: "#666666",
        selectors: {
          [HighContrastSelector]: {
            color: "HighlightText",
          },
        },
      },
      subMenuIcon: {
        height: ContextualMenuItemHeight,
        lineHeight: ContextualMenuItemHeight,
        textAlign: "center",
        display: "inline-block",
        verticalAlign: "middle",
        flexShrink: "0",
        fontSize: 10,
      },
      splitButtonFlexContainer: {
        display: "flex",
        height: ContextualMenuItemHeight,
        flexWrap: "nowrap",
        justifyContent: "center",
        alignItems: "center",
      },
    };

    return concatStyleSets(menuItemStyles);
  }
);
